// useJobApplicationDetailsViewModel.ts
import { useEffect, useReducer, useRef } from 'react';
import {  initialState, JobApplicationDetailsState } from '../model/JobApplicationDetailsState';
import { reducer} from '../model/JobApplicationDetailsReducer';
import { JobApplicationsApi } from '../../../candidate/jobApplications/api/jobApplicationsApi';

export const useJobApplicationDetailsViewModel = (
  applicationId?: string
): {
  state: JobApplicationDetailsState;
  handleWithdraw: () => Promise<void>;
  handleDownloadResume: () => Promise<void>;
  clearError: () => void;
  removeToast: (id: number) => void;
  clearToasts: () => void;
} => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!applicationId || hasFetchedRef.current) return;

    hasFetchedRef.current = true;

    const loadDetails = async () => {
      dispatch({ type: 'FETCH_START' });

      try {
        const application = await JobApplicationsApi.getApplicationById(applicationId);
        dispatch({ type: 'FETCH_SUCCESS', payload: application });
      } catch (err: any) {
        dispatch({
          type: 'FETCH_ERROR',
          payload: err?.message || 'Failed to load application',
        });
      }
    };

    loadDetails();
  }, [applicationId]);

  // Toast helper functions
  const addToast = (message: string, type: 'success' | 'error' | 'info' | 'warning') => {
    dispatch({ type: 'ADD_TOAST', payload: { message, type } });
    
    // Auto remove toast after 5 seconds
    const toastId = Date.now();
    setTimeout(() => {
      dispatch({ type: 'REMOVE_TOAST', payload: toastId });
    }, 5000);
  };

  const removeToast = (id: number) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  };

  const clearToasts = () => {
    dispatch({ type: 'CLEAR_TOASTS' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const handleWithdraw = async (): Promise<void> => {
    if (!applicationId) return;

    // Don't allow withdrawal if already withdrawn
    if (state.application?.status === 'withdrawn') {
      addToast('Application has already been withdrawn', 'info');
      return;
    }

    dispatch({ type: 'WITHDRAW_START' });

    try {
      // Call withdrawal API
      await JobApplicationsApi.withdrawApplication(applicationId);
      
      // Refresh the application data to get updated status
      const updatedApplication = await JobApplicationsApi.getApplicationById(applicationId);
      
      dispatch({ type: 'WITHDRAW_SUCCESS', payload: updatedApplication });
      addToast('Application withdrawn successfully!', 'success');
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to withdraw application. Please try again.';
      dispatch({ type: 'WITHDRAW_ERROR', payload: errorMessage });
      addToast(errorMessage, 'error');
    }
  };

  const handleDownloadResume = async (): Promise<void> => {
    if (!state.application?.resume) {
      addToast('No resume available for download', 'info');
      return;
    }

    dispatch({ type: 'DOWNLOAD_START' });

    try {
      const filename = state.application.resume.filename;
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Create a mock file download
      const blob = new Blob(['Mock resume content for: ' + filename], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      dispatch({ type: 'DOWNLOAD_SUCCESS' });
      addToast('Resume downloaded successfully!', 'success');
      
    } catch (err: any) {
      const errorMessage = 'Failed to download resume. Please try again.';
      dispatch({ type: 'DOWNLOAD_ERROR', payload: errorMessage });
      addToast(errorMessage, 'error');
    }
  };

  return { 
    state, 
    handleWithdraw,
    handleDownloadResume,
    clearError,
    removeToast,
    clearToasts,
  };
};